<template>
  <div class="map-widget">
    <div class="widget-header">
      <div class="header-title">
        <h2>{{ title }}</h2>
        <p v-if="subtitle">{{ subtitle }}</p>
      </div>
      <div class="controls-row">
        <button v-if="viewMode === 'map'" class="zoom-btn" @click="zoomIn" title="Zoom in">+</button>
        <button v-if="viewMode === 'map'" class="zoom-btn" @click="zoomOut" title="Zoom out">−</button>
        <button v-if="viewMode === 'map' && isZoomed" class="zoom-btn" @click="resetZoom" title="Reset">⤾</button>
        <button class="toggle-btn" @click="viewMode = viewMode === 'map' ? 'table' : 'map'">
          {{ viewMode === 'map' ? t('viewTable', lang) : t('viewMap', lang) }}
        </button>
        <button v-if="viewMode === 'table'" class="export-btn" @click="exportToExcel">
          {{ t('exportXlsx', lang) }}
        </button>
      </div>
    </div>

    <div class="map-container">
      <template v-if="viewMode === 'map'">
        <div
          v-if="hoveredFeature"
          class="tooltip"
          :style="{ top: tooltipPos.y + 'px', left: tooltipPos.x + 'px' }"
        >
          <strong>{{ featName(hoveredFeature) }}</strong>
          <br />
          {{ valueLabel }}: {{ getCount(hoveredFeature).toLocaleString('es-ES') }}
          <template v-if="metricLines(hoveredFeature).length">
            <div class="tooltip-sep"></div>
            <div v-for="(ml, mi) in metricLines(hoveredFeature)" :key="mi" class="tooltip-metric">
              <span>{{ ml.name }}</span><b>{{ ml.value }}</b>
            </div>
          </template>
        </div>

        <svg
          ref="svgEl"
          :viewBox="viewBoxStr"
          class="world-map"
          :class="{ panning: isPanning }"
          @mousemove="onMouseMove"
          @mousedown="onMouseDown"
          @mouseup="onMouseUp"
          @mouseleave="onMouseUp"
          @wheel.prevent="onWheel"
          @dblclick="resetZoom"
        >
          <g>
            <path
              v-for="(item, index) in featuresWithColors"
              :key="index"
              :d="item.path"
              :fill="item.color"
              class="country-path"
              @mouseenter="hoveredFeature = item.feat"
              @mouseleave="hoveredFeature = null"
            ></path>
          </g>
        </svg>
      </template>

      <div v-else class="table-container">
        <table class="data-table">
          <thead>
            <tr>
              <th>{{ countryLabel }}</th>
              <th>{{ valueLabel }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, index) in tableData" :key="index">
              <td>{{ row.country }}</td>
              <td>{{ row.value !== null && row.value !== undefined ? row.value.toLocaleString('es-ES') : 'N/A' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, shallowRef, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import { t, normalizeLang } from '../i18n/index.js';
import { debugLog, debugWarn } from '../utils/debug.js';
import { feature } from 'topojson-client';
import { geoNaturalEarth1, geoPath } from 'd3-geo';
import * as XLSX from 'xlsx';
import topoData from 'world-atlas/countries-110m.json';
import { COUNTRY_ALIASES } from '../data/countryAliases.js';
import { ISO_NUMERIC_TO_ALPHA2 } from '../data/isoNumericToAlpha2.js';

const props = defineProps({
  data: { type: Array, default: () => [] },
  dataKeyField: { type: String, default: 'country' },
  valueField: { type: String, default: 'count' },
  title: { type: String, default: '' },
  subtitle: { type: String, default: '' },
  countryLabel: { type: String, default: 'País' },
  valueLabel: { type: String, default: 'Valor' },
  // Agregades numèriques opcionals: [{ field, funcs:[SUM|AVG|MIN|MAX] }] i el mapa
  // de labels resolts pels serveis (cleanKey→text) per pintar-les al tooltip del país.
  metrics: { type: Array, default: () => [] },
  metricLabels: { type: Object, default: () => ({}) },
  lang: { type: String, default: 'ca_es' }
});

const METRIC_FUNC_LABELS = { SUM: 'Suma', AVG: 'Media', MIN: 'Mínimo', MAX: 'Máximo', MEDIAN: 'Mediana' };

const features = shallowRef([]);
const pathGenerator = shallowRef(null);
const width = ref(960);
const height = ref(500);
const hoveredFeature = shallowRef(null);
const tooltipPos = ref({ x: 0, y: 0 });
const counts = shallowRef({});
const countsNorm = shallowRef({});
// Fila completa per país (per llegir-ne les agregades al tooltip), normalitzada com counts.
const rowsNorm = shallowRef({});
const minVal = ref(0);
const maxVal = ref(1);
const viewMode = ref('map');

const svgEl = ref(null);
const view = ref({ x: 0, y: 0, w: 960, h: 500 });
const isPanning = ref(false);
const panOrigin = ref({ x: 0, y: 0, vx: 0, vy: 0 });
const MIN_ZOOM_W = 96;
const MAX_ZOOM_W = 1920;
const viewBoxStr = computed(() => `${view.value.x} ${view.value.y} ${view.value.w} ${view.value.h}`);
const isZoomed = computed(() => view.value.w !== width.value || view.value.x !== 0 || view.value.y !== 0);

const normalizeKey = (s) => String(s ?? '')
  .toLowerCase()
  .normalize('NFD').replace(/[̀-ͯ]/g, '')
  .replace(/[^a-z0-9]/g, '')
  .trim();

// --- Matching per codi ISO (robust, independent del nom) ------------------
// El feature del topo porta el codi ISO 3166-1 NUMÈRIC a `id` (China = "156");
// la dimensió `country` de Sherpa projecta l'ALPHA-2 ("cn"). Resolem l'alpha-2
// del feature i casem contra les dades per codi, evitant els problemes de noms
// localitzats ("República Popular China" ≠ "China").
function isoOf(feat) {
  const id = feat && (feat.id ?? feat.properties?.id);
  if (id == null) return null;
  return ISO_NUMERIC_TO_ALPHA2[String(id)] || null;
}

// Noms de país localitzats (ca/es/en) via Intl.DisplayNames, per mostrar un nom
// llegible quan les dades arriben com a codi ISO (taula, tooltip, export).
// SherpaCRM passa el lang en majúscules ("es_ES") → normalitzar com la resta de textos.
const localeForLang = (l) => ({ ca_es: 'ca', es_es: 'es', en_gb: 'en' }[normalizeLang(l)] || 'en');
const regionNames = computed(() => {
  try { return new Intl.DisplayNames([localeForLang(props.lang)], { type: 'region' }); }
  catch { return null; }
});
function isoName(iso) {
  const code = String(iso ?? '').toUpperCase();
  const rn = regionNames.value;
  if (rn) { try { const n = rn.of(code); if (n && n.toUpperCase() !== code) return n; } catch { /* unknown code */ } }
  return null;
}
// Etiqueta a mostrar per a una clau de dades: si és un codi alpha-2 conegut,
// el nom localitzat; si no, la clau tal qual (compat amb dimensions per nom).
function displayCountry(key) {
  const k = String(key ?? '');
  if (/^[a-zA-Z]{2}$/.test(k)) return isoName(k) || k;
  return k;
}
// Nom del feature per al tooltip: localitzat via el seu ISO, amb fallback al
// nom (anglès) del topo.
function featName(feat) {
  return isoName(isoOf(feat)) || feat?.properties?.name || '';
}

const tableData = computed(() =>
  Object.entries(counts.value)
    .map(([country, value]) => ({ country: displayCountry(country), value }))
    .sort((a, b) => (b.value ?? 0) - (a.value ?? 0))
);

const featurePaths = computed(() => {
  const pg = pathGenerator.value;
  const feats = features.value;
  if (!pg || feats.length === 0) return [];
  return feats.map(f => pg(f));
});

const featuresWithColors = computed(() => {
  const feats = features.value;
  const paths = featurePaths.value;
  if (paths.length === 0) return [];
  return feats.map((feat, i) => ({
    feat,
    path: paths[i],
    color: computeColor(feat)
  }));
});

function lookupCount(featName) {
  if (featName == null) return 0;
  const direct = counts.value[featName];
  if (direct !== undefined) return direct;
  const key = normalizeKey(featName);
  if (countsNorm.value[key] !== undefined) return countsNorm.value[key];
  // Candidates = the topo name + its known aliases (ISO codes + localized base names).
  const candidates = [key, ...(COUNTRY_ALIASES[key] || []).map(normalizeKey)];
  const normKeys = Object.keys(countsNorm.value);
  for (const cand of candidates) {
    if (countsNorm.value[cand] !== undefined) return countsNorm.value[cand];
    // The countryLanguage dimension returns the *full* localized name from
    // app_country (e.g. "Estados Unidos de América (los)", "United States of
    // America (the)", "Reino Unido de Gran Bretaña..."), which extends a known
    // base name with a suffix. Match those by prefix. Length >= 5 avoids false
    // positives from 2-letter ISO aliases (e.g. 'es' would match 'estonia').
    if (cand.length >= 5) {
      const hit = normKeys.find(nk => nk.startsWith(cand));
      if (hit !== undefined) return countsNorm.value[hit];
    }
  }
  return 0;
}

// Resol el nom del feature (topo) a la clau normalitzada de les dades (mateixa
// lògica d'àlies/prefix que lookupCount) per poder llegir la FILA i les seves agregades.
function resolveNormKey(featName) {
  if (featName == null) return null;
  const key = normalizeKey(featName);
  if (countsNorm.value[key] !== undefined) return key;
  const candidates = [key, ...(COUNTRY_ALIASES[key] || []).map(normalizeKey)];
  const normKeys = Object.keys(countsNorm.value);
  for (const cand of candidates) {
    if (countsNorm.value[cand] !== undefined) return cand;
    if (cand.length >= 5) {
      const hit = normKeys.find(nk => nk.startsWith(cand));
      if (hit !== undefined) return hit;
    }
  }
  return null;
}

function lookupRow(featName) {
  const key = resolveNormKey(featName);
  return key ? rowsNorm.value[key] : null;
}

// Variants feature-aware: proven PRIMER el codi ISO del feature (match robust)
// i cauen al match per nom/àlies/prefix si les dades no venen com a codi.
function lookupCountFeat(feat) {
  const iso = isoOf(feat);
  if (iso && countsNorm.value[iso] !== undefined) return countsNorm.value[iso];
  return lookupCount(feat?.properties?.name);
}

function lookupRowFeat(feat) {
  const iso = isoOf(feat);
  if (iso && rowsNorm.value[iso] !== undefined) return rowsNorm.value[iso];
  return lookupRow(feat?.properties?.name);
}

// Línies d'agregada per al tooltip del país: [{ name, value }] per (camp × funció).
function metricLines(feat) {
  if (!props.metrics || !props.metrics.length || !feat) return [];
  const row = lookupRowFeat(feat);
  const lines = [];
  for (const m of props.metrics) {
    if (!m || !m.field || !Array.isArray(m.funcs)) continue;
    const leaf = String(m.field).split('.').pop();
    for (const func of m.funcs) {
      const expr = `${func}(${m.field})`;
      const cleanKey = expr.replace(/[\[\]" ]/g, '').toLowerCase();
      const name = props.metricLabels[cleanKey] || ((METRIC_FUNC_LABELS[func] || func) + ' ' + leaf);
      const v = row ? row[expr] : undefined;
      lines.push({ name, value: (v === undefined || v === null) ? '—' : Number(v).toLocaleString('es-ES', { maximumFractionDigits: 2 }) });
    }
  }
  return lines;
}

function computeColor(feat) {
  const count = lookupCountFeat(feat);
  if (!count) return '#e5e7eb';
  const range = maxVal.value - minVal.value || 1;
  const ratio = (count - minVal.value) / range;
  const opacity = 0.2 + (ratio * 0.8);
  return `rgba(11, 83, 148, ${opacity})`;
}

function ingestData() {
  if (!props.data || props.data.length === 0) {
    counts.value = {};
    countsNorm.value = {};
    rowsNorm.value = {};
    return;
  }
  const direct = {};
  const norm = {};
  const rows = {};
  props.data.forEach(item => {
    const key = item[props.dataKeyField];
    const value = item[props.valueField];
    if (key && value !== undefined && value !== null) {
      direct[key] = value;
      norm[normalizeKey(key)] = value;
      rows[normalizeKey(key)] = item;
    }
  });
  counts.value = direct;
  countsNorm.value = norm;
  rowsNorm.value = rows;
  const values = Object.values(direct).filter(v => typeof v === 'number');
  minVal.value = 0;
  maxVal.value = values.length ? Math.max(...values) : 1;

  if (features.value.length) {
    const apiKeys = Object.keys(direct);
    const matched = features.value.filter(f => lookupCountFeat(f) > 0).length;
    debugLog('[WorldMapWidget] api keys:', apiKeys.slice(0, 8));
    debugLog('[WorldMapWidget] matched features:', matched, '/', apiKeys.length, 'api keys');
    if (apiKeys.length && matched === 0) {
      debugWarn('[WorldMapWidget] no country matched — first item:', props.data[0]);
    }
  }
}

watch(() => props.data, ingestData);

function initMap() {
  const objectKey = Object.keys(topoData.objects)[0];
  const geoJson = feature(topoData, topoData.objects[objectKey]);
  const projection = geoNaturalEarth1().fitSize([width.value, height.value], geoJson);
  pathGenerator.value = geoPath().projection(projection);
  features.value = Object.freeze(geoJson.features);
  view.value = { x: 0, y: 0, w: width.value, h: height.value };
  ingestData();
}

function getCount(feat) {
  return lookupCountFeat(feat);
}

let rafId = null;
function updateTooltipPos(event) {
  if (rafId) return;
  const x = event.clientX + 15;
  const y = event.clientY + 15;
  rafId = requestAnimationFrame(() => {
    tooltipPos.value = { x, y };
    rafId = null;
  });
}

function svgPointFromClient(clientX, clientY) {
  const el = svgEl.value;
  if (!el) return { x: 0, y: 0 };
  const rect = el.getBoundingClientRect();
  const px = (clientX - rect.left) / rect.width;
  const py = (clientY - rect.top) / rect.height;
  return {
    x: view.value.x + px * view.value.w,
    y: view.value.y + py * view.value.h
  };
}

function zoomAt(clientX, clientY, scale) {
  const newW = Math.max(MIN_ZOOM_W, Math.min(MAX_ZOOM_W, view.value.w * scale));
  const ratio = newW / view.value.w;
  if (ratio === 1) return;
  const p = svgPointFromClient(clientX, clientY);
  const newH = view.value.h * ratio;
  view.value = {
    x: p.x - (p.x - view.value.x) * ratio,
    y: p.y - (p.y - view.value.y) * ratio,
    w: newW,
    h: newH
  };
}

function onWheel(event) {
  const scale = event.deltaY < 0 ? 0.85 : 1.15;
  zoomAt(event.clientX, event.clientY, scale);
}

function zoomIn() {
  const el = svgEl.value;
  if (!el) return;
  const rect = el.getBoundingClientRect();
  zoomAt(rect.left + rect.width / 2, rect.top + rect.height / 2, 0.7);
}

function zoomOut() {
  const el = svgEl.value;
  if (!el) return;
  const rect = el.getBoundingClientRect();
  zoomAt(rect.left + rect.width / 2, rect.top + rect.height / 2, 1.4);
}

function resetZoom() {
  view.value = { x: 0, y: 0, w: width.value, h: height.value };
}

function onMouseDown(event) {
  if (event.button !== 0) return;
  isPanning.value = true;
  panOrigin.value = { x: event.clientX, y: event.clientY, vx: view.value.x, vy: view.value.y };
}

function onMouseMove(event) {
  if (isPanning.value) {
    const el = svgEl.value;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const dx = (event.clientX - panOrigin.value.x) / rect.width * view.value.w;
    const dy = (event.clientY - panOrigin.value.y) / rect.height * view.value.h;
    view.value = {
      x: panOrigin.value.vx - dx,
      y: panOrigin.value.vy - dy,
      w: view.value.w,
      h: view.value.h
    };
  } else {
    updateTooltipPos(event);
  }
}

function onMouseUp() {
  isPanning.value = false;
}

onMounted(() => initMap());
onBeforeUnmount(() => { if (rafId) cancelAnimationFrame(rafId); });

function exportToExcel() {
  const ws = XLSX.utils.json_to_sheet(tableData.value.map(r => ({ [props.countryLabel]: r.country, [props.valueLabel]: r.value })));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Countries');
  XLSX.writeFile(wb, 'mapa_mon.xlsx');
}
</script>

<style scoped>
.map-widget {
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03);
  position: relative;
  overflow-x: hidden;
  height: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
}

.map-widget .map-container {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  position: relative;
}

.widget-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
}

.widget-header h2 {
  font-size: 1.25rem;
  font-weight: 600;
  color: #111827;
  margin-top: 0;
  margin-bottom: 4px;
}

.widget-header p {
  font-size: 0.875rem;
  color: #6b7280;
  margin-top: 0;
  margin-bottom: 16px;
}

.world-map {
  width: 100%;
  height: auto;
  max-height: 500px;
  display: block;
  cursor: grab;
  user-select: none;
}

.world-map.panning {
  cursor: grabbing;
}

.zoom-btn {
  width: 28px;
  height: 28px;
  padding: 0;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
  background-color: #f9fafb;
  font-size: 1rem;
  line-height: 1;
  color: #374151;
  cursor: pointer;
  transition: all 0.2s;
}

.zoom-btn:hover {
  background-color: #f3f4f6;
  border-color: #d1d5db;
}

.country-path {
  stroke: #ffffff;
  stroke-width: 0.5px;
  transition: all 0.15s ease;
  cursor: pointer;
}

.country-path:hover {
  stroke: #1f2937;
  stroke-width: 1.5px;
  fill: #f59e0b !important;
}

.tooltip {
  position: fixed;
  background-color: rgba(17, 24, 39, 0.9);
  color: white;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 0.875rem;
  pointer-events: none;
  z-index: 1000;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  min-width: 150px;
}

.tooltip-sep {
  border-top: 1px solid rgba(255, 255, 255, 0.25);
  margin: 5px 0;
}

.tooltip-metric {
  display: flex;
  justify-content: space-between;
  gap: 18px;
  font-size: 0.8rem;
  line-height: 1.5;
}

.tooltip-metric span { color: #cbd5e1; }

.controls-row {
  display: flex;
  gap: 8px;
  align-items: center;
}

.toggle-btn,
.export-btn {
  padding: 6px 12px;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
  background-color: #f9fafb;
  font-size: 0.875rem;
  color: #374151;
  outline: none;
  cursor: pointer;
  transition: all 0.2s;
}

.toggle-btn:hover { background-color: #f3f4f6; border-color: #d1d5db; }
.export-btn { background-color: #10b981; color: white; border-color: #059669; }
.export-btn:hover { background-color: #059669; border-color: #047857; }

.table-container {
  max-height: 480px;
  overflow-y: auto;
  overflow-x: hidden;
  margin-top: 10px;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}

.data-table th,
.data-table td {
  padding: 12px;
  text-align: left;
  border-bottom: 1px solid #e5e7eb;
}

.data-table th {
  background-color: #f9fafb;
  font-weight: 600;
  color: #374151;
  position: sticky;
  top: 0;
}

.data-table tr:hover { background-color: #f9fafb; }
</style>
